/**
 * WorkflowDefinition Engine — v1 types
 *
 * Canonical schema shared by UI, worker, and server functions.
 * Zod schemas provide runtime validation in worker + server fn.
 */

import { z } from "zod";

// ─── Taxonomy ───────────────────────────────────────────────────────────────

export type WorkflowCategory = "automation" | "analysis" | "reporting" | "integration";
export type WorkflowIconKey = "clock" | "zap" | "workflow" | "briefcase" | "bar-chart";

// ─── Steps (discriminated union) ────────────────────────────────────────────

export type WorkflowStep =
  | {
      kind: "specialist_call";
      label: string;
      /** Matches Brain routing key, e.g. "task-management", "financial-summary" */
      specialist: string;
      inputs?: Record<string, unknown>;
    }
  | {
      kind: "emit_event";
      label: string;
      domain: string;
      type: string;
      payload?: Record<string, unknown>;
    }
  | {
      kind: "human_review";
      label: string;
      reason: string;
    }
  | {
      /** Suspends the run on a latent durable promise until a matching
       *  (domain, type) event arrives in the same tenant scope. */
      kind: "wait_for_event";
      label: string;
      domain: string;
      type: string;
    };

// ─── Preflight checks ───────────────────────────────────────────────────────

export type WorkflowPreflightCheck =
  | "requires_google_gemini"
  | "requires_calendar"
  | "requires_xero"
  | "requires_org_context";

// ─── WorkflowDefinition ─────────────────────────────────────────────────────

export interface WorkflowDefinition {
  /** Stable slug — never changes, used as queue payload identifier */
  id: string;
  name: string;
  description: string;
  category: WorkflowCategory;
  /** Open string — keep flexible */
  domain: string;
  /** Increment when steps change — tracked in cortex_task_log.context */
  version: number;
  iconKey: WorkflowIconKey;
  preflight: WorkflowPreflightCheck[];
  steps: WorkflowStep[];
}

// ─── Zod schema (runtime validation in worker + server fn) ──────────────────

export const workflowStepSchema: z.ZodType<WorkflowStep> = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("specialist_call"),
    label: z.string(),
    specialist: z.string(),
    inputs: z.record(z.unknown()).optional(),
  }),
  z.object({
    kind: z.literal("emit_event"),
    label: z.string(),
    domain: z.string(),
    type: z.string(),
    payload: z.record(z.unknown()).optional(),
  }),
  z.object({
    kind: z.literal("human_review"),
    label: z.string(),
    reason: z.string(),
  }),
  z.object({
    kind: z.literal("wait_for_event"),
    label: z.string(),
    domain: z.string(),
    type: z.string(),
  }),
]);

export const workflowDefinitionSchema = z.object({
  id: z.string().min(1).regex(/^wf-[a-z0-9-]+$/, "id must start with wf-"),
  name: z.string().min(1),
  description: z.string(),
  category: z.enum(["automation", "analysis", "reporting", "integration"]),
  domain: z.string(),
  version: z.number().int().positive(),
  iconKey: z.enum(["clock", "zap", "workflow", "briefcase", "bar-chart"]),
  preflight: z.array(z.enum([
    "requires_google_gemini",
    "requires_calendar",
    "requires_xero",
    "requires_org_context",
  ])),
  steps: z.array(workflowStepSchema).min(1),
});

// ─── WorkflowRun payload (lives inside cortexContext in queue message) ───────

export interface WorkflowRunPayload {
  workflowId: string;
  workflowVersion: number;
  workflowName: string;
  runMode: "async";
  /**
   * Frozen definition snapshot — the durable executor (workflow-runner)
   * interprets exactly these steps; registry edits can never mutate a live
   * run's replay.
   */
  steps: WorkflowStep[];
  inputs?: Record<string, unknown>;
}

export const workflowRunPayloadSchema = z.object({
  workflowId: z.string(),
  workflowVersion: z.number().int(),
  workflowName: z.string(),
  runMode: z.literal("async"),
  steps: z.array(workflowStepSchema).min(1),
  inputs: z.record(z.unknown()).optional(),
});

// ─── UI display type (mapped by useWorkflows, keeps React out of shared pkg) ─

export interface WorkflowUIItem {
  id: string;
  name: string;
  description: string;
  category: WorkflowCategory;
  domain: string;
  iconKey: WorkflowIconKey;
  stepCount: number;
  version: number;
  preflight: WorkflowPreflightCheck[];
}
